package com.unrealmi.site

import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.background
import com.varabyte.kobweb.silk.theme.colors.palette.color

/**
 * Design tokens for the UnrealMi site.
 *
 * The palette leans into a "tech" aesthetic built around a violet/indigo brand, layering translucent
 * surfaces, soft borders and colored glows on top of a near-black (dark) or near-white (light) canvas.
 *
 * @property nearBackground Slightly offset from the page background; use for bars and panels that need to
 *   separate themselves from the canvas just a little.
 * @property surface Translucent card/panel fill, designed to sit over the animated background.
 * @property surfaceStrong A more opaque surface for content that needs better contrast.
 * @property border Hairline border color for cards and dividers.
 * @property borderStrong A more visible border, e.g. on hover.
 * @property subtle Muted foreground color for secondary/supporting text.
 * @property glow Ambient glow color used behind hero visuals and accents.
 * @property grid Color of the faint background grid lines.
 */
class SitePalette(
    val nearBackground: Color,
    val surface: Color,
    val surfaceStrong: Color,
    val border: Color,
    val borderStrong: Color,
    val subtle: Color,
    val glow: Color,
    val grid: Color,
    val cobweb: Color,
    val brand: Brand,
) {
    class Brand(
        // Bright violet – primary call-to-action / highlight color.
        val primary: Color = Color.rgb(0xB568FE),
        // Deep indigo – used as the darker end of gradients.
        val accent: Color = Color.rgb(0x4821CB),
        // Mid violet – bridges primary and accent for smoother gradients.
        val mid: Color = Color.rgb(0x7C5CFF),
        // Electric cyan – sparingly used tech highlight for contrast against the purples.
        val cyan: Color = Color.rgb(0x38BDF8),
    )
}

object SitePalettes {
    val light = SitePalette(
        nearBackground = Color.rgb(0xF4F6FA),
        surface = Color.rgba(0xFF, 0xFF, 0xFF, 0.72f),
        surfaceStrong = Color.rgba(0xFF, 0xFF, 0xFF, 0.92f),
        border = Color.rgba(0x48, 0x21, 0xCB, 0.14f),
        borderStrong = Color.rgba(0xB5, 0x68, 0xFE, 0.55f),
        subtle = Color.rgba(0x1A, 0x1F, 0x2B, 0.66f),
        glow = Color.rgba(0xB5, 0x68, 0xFE, 0.35f),
        grid = Color.rgba(0x48, 0x21, 0xCB, 0.07f),
        cobweb = Colors.LightGray,
        brand = SitePalette.Brand(),
    )
    val dark = SitePalette(
        nearBackground = Color.rgb(0x0D1017),
        surface = Color.rgba(0xFF, 0xFF, 0xFF, 0.04f),
        surfaceStrong = Color.rgba(0xFF, 0xFF, 0xFF, 0.07f),
        border = Color.rgba(0xB5, 0x68, 0xFE, 0.18f),
        borderStrong = Color.rgba(0xB5, 0x68, 0xFE, 0.60f),
        subtle = Color.rgba(0xE6, 0xE9, 0xF2, 0.62f),
        glow = Color.rgba(0xB5, 0x68, 0xFE, 0.45f),
        grid = Color.rgba(0xB5, 0x68, 0xFE, 0.06f),
        cobweb = Colors.LightGray.inverted(),
        brand = SitePalette.Brand(),
    )
}

fun ColorMode.toSitePalette(): SitePalette {
    return when (this) {
        ColorMode.LIGHT -> SitePalettes.light
        ColorMode.DARK -> SitePalettes.dark
    }
}

@InitSilk
fun initTheme(ctx: InitSilkContext) {
    ctx.theme.palettes.light.background = Color.rgb(0xF7F8FC)
    ctx.theme.palettes.light.color = Color.rgb(0x14181F)
    ctx.theme.palettes.dark.background = Color.rgb(0x06080B)
    ctx.theme.palettes.dark.color = Color.rgb(0xF2F4FA)
}
